import { buildApiUrl, fetchJSON, validateRequiredParams } from '../crm/crmApi';

export interface SupplierInput {
  organization_id: string;
  name: string;
  category?: string;
  status?: 'active' | 'onboarded' | 'on_hold' | 'blacklisted';
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
}

export interface Supplier extends SupplierInput {
  id: string;
  owner_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ListSuppliersParams {
  organization_id: string;
  search?: string;
  status?: 'active' | 'onboarded' | 'on_hold' | 'blacklisted';
  limit?: number;
  offset?: number;
}

const suppliersService = {
  async list(params: ListSuppliersParams): Promise<{ data: Supplier[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'supplierCrm.suppliersService.list'
    });
    const url = buildApiUrl('/api/supplier-crm/suppliers', params as any);
    return fetchJSON(url, {
      description: 'List supplier CRM suppliers',
      context: { organization_id: params.organization_id, search: params.search, status: params.status }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: Supplier | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierCrm.suppliersService.get'
    });
    const url = buildApiUrl(`/api/supplier-crm/suppliers/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get supplier CRM supplier',
      context: { id, organization_id }
    });
  },
  async create(payload: SupplierInput): Promise<{ data: Supplier }>{
    validateRequiredParams(payload, ['organization_id', 'name'], {
      service: 'supplierCrm.suppliersService.create'
    });
    const url = buildApiUrl('/api/supplier-crm/suppliers');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create supplier CRM supplier',
      context: { organization_id: payload.organization_id }
    });
  },
  async update(id: string, payload: Partial<SupplierInput> & { organization_id: string }): Promise<{ data: Supplier }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'supplierCrm.suppliersService.update'
    });
    const url = buildApiUrl(`/api/supplier-crm/suppliers/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update supplier CRM supplier',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierCrm.suppliersService.remove'
    });
    const url = buildApiUrl(`/api/supplier-crm/suppliers/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete supplier CRM supplier',
      context: { id, organization_id }
    });
  }
};

export default suppliersService;
